import react, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc, deleteDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import '../Styles/User/profileStyle.css';

function Profile() {
    const [createdAuctions, setCreatedAuctions] = useState([]);
    const [pastAuctions, setPastAuctions] = useState([]);
    const [userData, setUserData] = useState(null);
    const [email, setEmail] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [postcode, setPostcode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isTccMember, setIsTccMember] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const auth = getAuth();
    const db = getFirestore();
    const storage = getStorage();

    const fetchUserData = async () => {
        const user = auth.currentUser;
        if (user) {
            try {
                const userDocRef = doc(db, 'Users', user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUserData(userData);
                    setAddressLine1(userData.AddressLine1 || '');
                    setAddressLine2(userData.AddressLine2 || '');
                    setPostcode(userData.Postcode || '');
                    setPhoneNumber(userData.PhoneNumber || '');
                    setFirstName(userData.FirstName || '');
                    setLastName(userData.LastName || '');
                    setEmail(userData.email || '');
                    setIsTccMember(userData.isTccMember || false);
                } else {
                    
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        }
    };

    const fetchCreatedAuctions = async () => {
        const user = auth.currentUser;
        if (user) {
            try {
                const auctionsQuery = query(
                    collection(db, 'AuctionItems'),
                    where('Author', '==', user.uid)
                );
                const querySnapshot = await getDocs(auctionsQuery);
                const auctions = [];
                querySnapshot.forEach((doc) => {
                    auctions.push({ id: doc.id, ...doc.data() });
                });
                setCreatedAuctions(auctions);
            } catch (error) {
                console.error('Error fetching created auctions:', error);
            }
        }
    };

    const fetchPastAuctions = async () => {
        const user = auth.currentUser;
        if (!user) {
            console.error("No user is currently authenticated.");
            return;
        }
    
        try {
            const auctionsCollectionRef = collection(db, 'AuctionItems');
            const querySnapshot = await getDocs(auctionsCollectionRef);
    
            const userId = user.uid;
            const matchingAuctions = [];
    
            querySnapshot.forEach((doc) => {
                const auctionData = doc.data();
                
                // Check if Rounds exist
                if (auctionData.Rounds) {
                    for (const [roundName, roundData] of Object.entries(auctionData.Rounds)) {
                        if (roundData.Participants) {
                            const isParticipant = roundData.Participants.some(
                                (participant) => participant.userId === userId
                            );
    
                            if (isParticipant) {
                                matchingAuctions.push({ id: doc.id, ...auctionData });
                                break;
                            }
                        }
                    }
                }
            });
    
            // Set the results to state
            setPastAuctions(matchingAuctions);
        } catch (error) {
            console.error('Error fetching past auctions:', error);
        }    
    };    

    const handleSave = async () => {
        const user = auth.currentUser;

        if (user) {
            try {
                const userDocRef = doc(db, 'Users', user.uid);
                await updateDoc(userDocRef, {
                    AddressLine1: addressLine1,
                    AddressLine2: addressLine2,
                    Postcode: postcode,
                    PhoneNumber: phoneNumber,
                    FirstName: firstName,
                    LastName: lastName,
                    isTccMember: isTccMember,
                });
                setSuccess('Updated successfully!');
                setError('');
            } catch (error) {
                console.error('Error updating address:', error);
                setError('Failed to update address.');
                setSuccess('');
            }
        }
    };

    const handleDeleteAccount = async () => {
        const user = auth.currentUser;
        if (user) {
            const confirmation = window.confirm(
                'Are you sure you want to delete your account? This action cannot be undone.'
            );
            if (confirmation) {
                try {
                    // Delete user's auctions
                    const auctionsQuery = query(
                        collection(db, 'AuctionItems'),
                        where('Author', '==', user.uid)
                    );
                    const querySnapshot = await getDocs(auctionsQuery);
                    const deleteAuctionPromises = querySnapshot.docs.map((doc) =>
                        deleteDoc(doc.ref)
                    );
                    await Promise.all(deleteAuctionPromises);

                    // Delete user data from Firestore
                    const userDocRef = doc(db, 'Users', user.uid);
                    await deleteDoc(userDocRef);

                    // Delete user's files from Firebase Storage (if any)
                    const userStorageRef = ref(storage, `Users/${user.uid}`);
                    await deleteObject(userStorageRef);

                    // Delete user account
                    await user.delete();
                    alert('Account deleted successfully.');
                } catch (error) {
                    console.error('Error deleting account:', error);
                    alert('Failed to delete account. Please try again later.');
                }
            }
        }
    };

    useEffect(() => {
        fetchUserData();
        fetchCreatedAuctions();
        fetchPastAuctions();
    }, []);


    return (
        <div className="profileContainer">
            <div className="header">
                <h2>{email}</h2>
            </div>
            <div className="personalInformationContainer">
                <h2>Personal Information</h2>
                <p>Change your information, ensure it is always accurate.</p>
                <div className="informationBoxes">
                    <label className="toggleContainer">
                        <span>TCC Membership:</span>
                        <div 
                            className={`toggleSwitch ${isTccMember ? 'active' : ''}`} 
                            onClick={() => setIsTccMember(!isTccMember)}
                        >
                            <div className="toggleThumb"></div>
                        </div>
                        {/* <span className="toggleStatus">{isTccMember ? 'Active' : 'Inactive'}</span> */}
                    </label>

                    <label>
                        Address Line 1: 
                        <input
                            type="text"
                            value={addressLine1}
                            onChange={(e) => setAddressLine1(e.target.value)}
                        />
                    </label>
                    <label>
                        Address Line 2: 
                        <input
                            type="text"
                            value={addressLine2}
                            onChange={(e) => setAddressLine2(e.target.value)}
                        />
                    </label>
                    <label>
                        Zipcode: 
                        <input
                            type="text"
                            value={postcode}
                            onChange={(e) => setPostcode(e.target.value)}
                        />
                    </label>
                    <label>
                        Phone Number:
                        <input
                            type="text"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="+441234567890"
                        />
                    </label>
                    <label>
                        First Name:
                        <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First Name"
                        />
                    </label>
                    <label>
                        Last Name:
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last Name"
                        />
                    </label>
                    <label><p></p> <button type="submit" className="saveBtn" onClick={handleSave}>Save</button></label>
                    {error && <p className="error">{error}</p>}
                    {success && <p className="success">{success}</p>}
                </div>
            </div>
            <div className="yourAuctionsContainer">
                <h2>Your Auctions</h2>
                <p>Your current & past created auction items</p>
                <div className="auctionContainer">
                    {createdAuctions.length > 0 ? (
                        createdAuctions.map((item) => (
                            <Link
                                key={item.id} 
                                to={`/auctions/${item.id}`}
                                style={{ textDecoration: 'none' }}
                            >
                                <div key={item.id} className="auctionItemCard">
                                    <img
                                        src={item.Photos[0]}
                                        alt={`Auction item`}
                                        className="auctionPhoto"
                                    />
                                    <div className="cardText">
                                        <h2>{item.Name}</h2>
                                        <p>{item.Location}</p>
                                    </div>
                                </div>
                            </Link>
                        ))
                    ) : (
                        <p>No created auctions found.</p>
                    )}
                </div>
            </div>
            <div className="historicalAuctions">
                <h2>Historical Auctions</h2>
                <p>Previous auctions bid on</p>
                <div className="auctionContainer">
                    {pastAuctions.length > 0 ? (
                        pastAuctions.map((item) => (
                            <Link
                                key={item.id} 
                                to={`/auctions/${item.id}`}
                                style={{ textDecoration: 'none' }}
                            >
                                <div key={item.id} className="auctionItemCard">
                                    <img
                                        src={item.Photos[0]}
                                        alt={`Auction item`}
                                        className="auctionPhoto"
                                    />
                                    <div className="cardText">
                                        <h2>{item.Name}</h2>
                                        <p>{item.Location}</p>
                                    </div>
                                </div>
                            </Link>
                        ))
                    ) : (
                        <p>No participated in auctions found.</p>
                    )}
                </div>
            </div>
            <div className="accountControls">
                <button onClick={handleDeleteAccount}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <g fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" d="M20.5 6h-17m6 5l.5 5m4.5-5l-.5 5"/><path d="M6.5 6h.11a2 2 0 0 0 1.83-1.32l.034-.103l.097-.291c.083-.249.125-.373.18-.479a1.5 1.5 0 0 1 1.094-.788C9.962 3 10.093 3 10.355 3h3.29c.262 0 .393 0 .51.019a1.5 1.5 0 0 1 1.094.788c.055.106.097.23.18.479l.097.291A2 2 0 0 0 17.5 6"/><path stroke-linecap="round" d="M18.374 15.4c-.177 2.654-.266 3.981-1.131 4.79s-2.195.81-4.856.81h-.774c-2.66 0-3.99 0-4.856-.81c-.865-.809-.953-2.136-1.13-4.79l-.46-6.9m13.666 0l-.2 3"/></g>
                    </svg>
                    Delete Account
                </button>
            </div>

            <Link 
                to={`/create-auction`}
                style={{ textDecoration: 'none' }}
            >
                <button className="createItemBtn">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z"/>
                    </svg>
                    Create Auction
                </button>
            </Link>
        </div>
    )
}

export default Profile;